import { fastapi } from './api';

export interface DemoStatus {
  allowed: boolean;
  active: boolean;
  expires_at: string | null;
  duration_seconds: number;
}

type DemoData = {
  version: number;
  expiresAt: string;
  threads: any[];
  messages: Record<string, any[]>;
  notes: Record<string, any[]>;
  tags: any[];
  folders: any[];
  threadTags: Record<string, number[]>;
  pinnedIds: string[];
  patients: any[];
  rooms: any[];
  doctors: any[];
  appointments: any[];
  schedules: any[];
  holidays: any[];
  nextId: number;
};

const STATUS_KEY = 'taratic:demo:status';
const DATA_VERSION = 2;

const tenantKey = () => localStorage.getItem('comercio_key') || localStorage.getItem('comercioKey') || 'tenant';
const dataKey = () => `taratic:demo:data:${tenantKey()}`;
const isoAgo = (minutes: number) => new Date(Date.now() - minutes * 60_000).toISOString();
const isoFromNow = (minutes: number) => new Date(Date.now() + minutes * 60_000).toISOString();

export function getDemoStatus(): DemoStatus | null {
  try {
    const status = JSON.parse(localStorage.getItem(STATUS_KEY) || 'null') as DemoStatus | null;
    return status;
  } catch {
    return null;
  }
}

export function setDemoStatus(status: DemoStatus): DemoStatus {
  localStorage.setItem(STATUS_KEY, JSON.stringify(status));
  return status;
}

export async function refreshDemoStatus(signal?: AbortSignal): Promise<DemoStatus> {
  const { data } = await fastapi.get('/tenants/me/demo', { signal, timeout: 15_000 });
  return setDemoStatus(data);
}

export function isDemoModeActive(): boolean {
  const status = getDemoStatus();
  // Keep routing to the isolated store until the status poll explicitly ends
  // the session and reloads. This avoids a short expiry race where stale demo
  // UI could otherwise send an action to a live endpoint.
  return !!(status?.allowed && status.active);
}

function imageAttachment() {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="420"><rect width="640" height="420" fill="#e8f4f8"/><rect x="72" y="60" width="496" height="300" rx="22" fill="white" stroke="#62a8bd" stroke-width="5"/><path d="M170 255c75-105 125-70 170 0 50-95 105-90 155 20" fill="none" stroke="#62a8bd" stroke-width="14"/><circle cx="220" cy="155" r="32" fill="#f4c7ab"/><text x="320" y="335" text-anchor="middle" font-family="Arial" font-size="22" fill="#315d6a">Imagen clínica de demostración</text></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function seed(expiresAt: string): DemoData {
  const people = [
    ['demo-101', 'Lucía Herrera', 'luciaherrera_24', 'Quería pedir una primera consulta', 2, 8, '/demo/clients/lucia-herrera.jpg'],
    ['demo-102', 'Carlos Medina', 'carlosmedina_81', '¿Cuál es el precio del tratamiento?', 1, 24, '/demo/clients/carlos-medina.jpg'],
    ['demo-103', 'Marta Silva', 'martasilva_35', 'Perfecto, confirmo la cita', 0, 52, '/demo/clients/marta-silva.jpg'],
    ['demo-104', 'Diego Ramos', 'diegoramos_17', '¿Podemos cambiarla al jueves?', 3, 95, '/demo/clients/diego-ramos.jpg'],
    ['demo-105', 'Elena Torres', 'elenatorres_62', 'Necesito cancelar la cita', 0, 190, '/demo/clients/elena-torres.jpg'],
    ['demo-106', 'Sofía Navarro', 'sofianavarro_09', 'Tengo dolor fuerte desde anoche', 4, 4, '/demo/clients/sofia-navarro.jpg'],
  ] as const;
  const tags = [
    { id: 9101, name: 'Primera consulta', color: '#7c3aed', folder_id: 9001 },
    { id: 9102, name: 'Seguimiento', color: '#0ea5e9', folder_id: 9001 },
    { id: 9103, name: 'Urgente', color: '#ef4444', folder_id: 9002 },
    { id: 9104, name: 'Pendiente', color: '#f59e0b', folder_id: 9002 },
    { id: 9105, name: 'Confirmada', color: '#10b981', folder_id: 9002 },
  ];
  const folders = [{ id: 9001, name: 'Pacientes demo', parent_id: null }, { id: 9002, name: 'Estado demo', parent_id: null }];
  const threads = people.map(([id, name, username, lastMessage, unreadCount, ago, avatarUrl], index) => ({
    id, customer_id: id, name, username, lastMessage, unreadCount, unread: unreadCount > 0,
    unreadClientCount: unreadCount, unreadBotCount: 0, updated_time: isoAgo(ago), avatarUrl,
    platform: 'instagram', replyWindowEndsAt: isoAgo(ago - 24 * 60),
    bot_enabled: index !== 5, selected_bot_id: 1, appointment_booked: index === 2,
    escalated_to_human: index === 5, escalation_reason: index === 5 ? 'Síntomas urgentes detectados' : null,
    patient: { id: 8001 + index, full_name: name },
    demo_scenario: ['primera consulta', 'precios de tratamiento', 'confirmación de cita', 'cambio de cita', 'cancelación de cita', 'derivación urgente'][index],
  }));
  const messages: Record<string, any[]> = {};
  const scripts = [
    ['Hola, es la primera vez que os escribo.', '¡Hola, Lucía! Encantada de ayudarte. ¿Qué tipo de consulta necesitas?', 'Quería pedir una primera consulta'],
    ['Buenos días, me interesa el tratamiento facial.', 'La valoración inicial cuesta 45 € y se descuenta del tratamiento.', '¿Cuál es el precio del tratamiento?'],
    ['¿Tenéis hueco esta semana?', 'Sí, te reservé el miércoles a las 17:00 con la Dra. Vega.', 'Perfecto, confirmo la cita'],
    ['Me surgió un imprevisto.', 'Sin problema. Podemos mover tu cita manteniendo el mismo servicio.', '¿Podemos cambiarla al jueves?'],
    ['No podré acudir mañana.', 'Puedo cancelarla ahora. Si quieres, te proponemos otra fecha.', 'Necesito cancelar la cita'],
    ['Hola, necesito ayuda con una molestia que empezó anoche.', 'Siento que estés así. Voy a pasar la conversación al equipo de la clínica.', 'Tengo dolor fuerte desde anoche'],
  ];
  people.forEach(([id], index) => {
    messages[id] = scripts[index].map((text, messageIndex) => ({
      id: `${id}-m${messageIndex + 1}`, fromMe: messageIndex === 1, text,
      imageUrl: index === 1 && messageIndex === 0 ? imageAttachment() : undefined,
      createdAt: isoAgo((people[index][5] as number) + (2 - messageIndex) * 3),
      senderKind: messageIndex === 1 ? 'bot' : 'human', origin: 'instagram', status: 'read',
    }));
  });
  const patients = people.map(([, name, , , , , avatarUrl], index) => ({
    id: 8001 + index, full_name: name, email: `${name.toLowerCase().replace(/ /g, '.')}@example.com`, phone: `+34 600 000 10${index}`,
    date_of_birth: null, gender: null, blood_type: null, allergies: null, chronic_conditions: null,
    emergency_contact_name: null, emergency_contact_phone: null, insurance_provider: null, insurance_policy_number: null,
    address: null, status: 'active', notes: null, avatar_url: avatarUrl, registration_date: isoAgo(1440 * (index + 2)),
    created_at: isoAgo(1440 * (index + 2)), updated_at: isoAgo(60), last_appointment_date: index > 3 ? isoAgo(1440 * 12) : null,
    next_appointment_date: index < 4 ? isoFromNow(240 + index * 360) : null, appointment_count: index + 1,
  }));
  const rooms = [
    { id: 'demo-room-1', name: 'Consulta 1', resource_type: 'consultation_room', capacity: 2, description: 'Consulta general', color_code: '#0ea5e9', is_active: true },
    { id: 'demo-room-2', name: 'Sala de tratamientos', resource_type: 'treatment_room', capacity: 3, description: 'Tratamientos', color_code: '#8b5cf6', is_active: true },
  ];
  const doctors = [
    { id: 8201, name: 'Dra. Ana Vega', specialty: 'Medicina estética', phone: '+34 600 100 001', email: 'ana.vega@example.com', bio: 'Profesional de la clínica', is_active: true, workdays: [], vacations: [], created_at: isoAgo(9000), updated_at: isoAgo(60) },
    { id: 8202, name: 'Dr. Pablo León', specialty: 'Dermatología', phone: '+34 600 100 002', email: 'pablo.leon@example.com', bio: 'Profesional de la clínica', is_active: true, workdays: [], vacations: [], created_at: isoAgo(9000), updated_at: isoAgo(60) },
  ];
  const appointmentSpecs = [
    [8001, 8201, 'Dra. Ana Vega', 180, 'consultation', 'pending'],
    [8003, 8201, 'Dra. Ana Vega', 1500, 'follow-up', 'confirmed'],
    [8004, 8202, 'Dr. Pablo León', 2940, 'checkup', 'pending'],
    [8002, 8202, 'Dr. Pablo León', 4380, 'consultation', 'confirmed'],
    [8005, 8201, 'Dra. Ana Vega', -720, 'follow-up', 'cancelled'],
  ] as const;
  const appointments = appointmentSpecs.map(([patientId, doctorId, doctorName, minutes, appointmentType, status], index) => ({
    id: 8301 + index, patient_id: patientId, patient_name: patients.find(p => p.id === patientId)?.full_name,
    room_id: rooms[index % rooms.length].id, start_time: isoFromNow(minutes), end_time: isoFromNow(minutes + 45),
    appointment_type: appointmentType, status, doctor_id: doctorId, doctor_name: doctorName,
    details: { chief_complaint: index === 0 ? 'Primera valoración' : 'Consulta de demostración', symptoms: null, diagnosis: null, treatment_plan: null, prescriptions: null, vitals_blood_pressure: null, vitals_heart_rate: null, vitals_temperature: null, vitals_weight: null, vitals_height: null, notes: 'Datos sintéticos para demo', follow_up_date: null },
    created_at: isoAgo(180), updated_at: isoAgo(30), tag_ids: status === 'confirmed' ? [9105] : status === 'pending' ? [9104] : [],
    tags: status === 'confirmed' ? [tags[4]] : status === 'pending' ? [tags[3]] : [],
  }));
  return {
    version: DATA_VERSION, expiresAt, threads, messages,
    notes: {
      'demo-101': [{ id: 8401, thread_id: 'demo-101', author_type: 'human', content: 'Prefiere contacto por la tarde.', created_at: isoAgo(90) }],
      'demo-106': [{ id: 8402, thread_id: 'demo-106', author_type: 'human', content: 'Handoff urgente: llamar si no responde.', created_at: isoAgo(3) }],
    },
    tags, folders,
    threadTags: { 'demo-101': [9101, 9104], 'demo-102': [9101, 9104], 'demo-103': [9102, 9105], 'demo-104': [9102, 9104], 'demo-105': [9102], 'demo-106': [9103, 9104] },
    pinnedIds: ['demo-106', 'demo-101'], patients, rooms, doctors, appointments,
    schedules: Array.from({ length: 5 }, (_, day) => ({ id: 8501 + day, resource_id: 8201, day_of_week: day + 1, start_time: '09:00', end_time: '18:00', default_appointment_duration: 45 })),
    holidays: [], nextId: 90000,
  };
}

export function getDemoData(): DemoData {
  if (!isDemoModeActive()) throw new Error('Demo mode is not active');
  const expiresAt = getDemoStatus()!.expires_at!;
  try {
    const stored = JSON.parse(localStorage.getItem(dataKey()) || 'null') as DemoData | null;
    if (stored?.version === DATA_VERSION && stored.expiresAt === expiresAt) return stored;
  } catch {}
  const created = seed(expiresAt);
  localStorage.setItem(dataKey(), JSON.stringify(created));
  return created;
}

export function saveDemoData(data: DemoData): DemoData {
  localStorage.setItem(dataKey(), JSON.stringify(data));
  window.dispatchEvent(new CustomEvent('taratic:demo-data-changed'));
  return data;
}

export function resetDemoData(): DemoData | null {
  localStorage.removeItem(dataKey());
  return isDemoModeActive() ? getDemoData() : null;
}

export const demoStore = {
  threads: () => getDemoData().threads,
  messages: (threadId: string) => getDemoData().messages[threadId] || [],
  notes: (threadId: string) => getDemoData().notes[threadId] || [],
  tags: () => ({ tags: getDemoData().tags, folders: getDemoData().folders }),
  threadTags: (threadId: string) => {
    const data = getDemoData();
    const ids = data.threadTags[threadId] || [];
    return data.tags.filter(tag => ids.includes(tag.id));
  },
  mutate: (fn: (data: DemoData) => void) => { const data = getDemoData(); fn(data); return saveDemoData(data); },
  nextId: (data: DemoData) => ++data.nextId,
};
